import React, { useMemo, useCallback } from "react";
import {
  HiOutlineChevronDown,
  HiOutlinePencilSquare,
} from "react-icons/hi2";
import { useSearch } from "../../../../contexts/SearchContext";
import { DYNAMIC_PACKAGES } from "../../../../contexts/SearchContext";
import { buildPlanesSearchSummary } from "../../../FlightBookingFlowMockup/adapters/flightSearchDisplayAdapter";
import "./FlightOfferCompactSummary.css";

/**
 * סיכום חיפוש טיסות קומפקטי + מצב accordion (שורה שלמה לחיצה לפתיחת עורך).
 */
export default function FlightOfferCompactSummary({
  onEditSearch,
  editorOpen = false,
  triggerLabel = "ערוך חיפוש",
  collapsedHint = "הצג פילטרים וחיפוש מלא",
  className = "",
  /** aria-controls על פאנל העורך (תוצאות טיסות) */
  accordionControlsId,
  /** id לכפתור הכותרת — aria-labelledby על הפאנל */
  accordionHeaderId,
}) {
  const {
    origin,
    location,
    calendarDateArrival,
    calendarDateLeave,
    roomInfo,
    flightTripType,
    searchTab,
  } = useSearch();

  const summary = useMemo(
    () =>
      buildPlanesSearchSummary({
        origin,
        location,
        calendarDateArrival,
        calendarDateLeave,
        roomInfo,
      }),
    [
      origin,
      location,
      calendarDateArrival,
      calendarDateLeave,
      roomInfo,
    ]
  );

  const tripLabel =
    flightTripType === "OneWay"
      ? "כיוון אחד"
      : flightTripType === "RoundTrip"
        ? "הלוך-חזור"
        : flightTripType || "—";

  const datesLine =
    summary.returnDate && summary.returnDate !== "—"
      ? `${summary.departureDate} → ${summary.returnDate}`
      : summary.departureDate;
  const variantClass =
    searchTab === DYNAMIC_PACKAGES
      ? "flight-offer-compact-summary--dynamic-packages"
      : "flight-offer-compact-summary--planes";

  const handleAccordionActivate = useCallback(() => {
    if (import.meta.env.DEV) {
      console.log("[FLIGHTS_SEARCH_ACCORDION_TOGGLE]", {
        editorOpenBefore: editorOpen,
        flow: accordionControlsId ? "flight-results-accordion" : "compact-search-strip",
        source: "FlightOfferCompactSummary",
      });
    }
    onEditSearch?.();
  }, [editorOpen, onEditSearch, accordionControlsId]);

  const summaryGrid = (
    <div className="flight-offer-compact-summary__scroll">
      <div className="flight-offer-compact-summary__grid">
        <div className="flight-offer-compact-summary__cell">
          <span className="flight-offer-compact-summary__label">מסלול</span>
          <span className="flight-offer-compact-summary__value">
            {summary.originLabel} → {summary.destinationLabel}
          </span>
        </div>
        <div className="flight-offer-compact-summary__cell">
          <span className="flight-offer-compact-summary__label">תאריכים</span>
          <span className="flight-offer-compact-summary__value">{datesLine}</span>
        </div>
        <div className="flight-offer-compact-summary__cell">
          <span className="flight-offer-compact-summary__label">נוסעים</span>
          <span className="flight-offer-compact-summary__value">
            {summary.passengersLabel}
          </span>
        </div>
        <div className="flight-offer-compact-summary__cell">
          <span className="flight-offer-compact-summary__label">סוג טיסה</span>
          <span className="flight-offer-compact-summary__value">{tripLabel}</span>
        </div>
      </div>
    </div>
  );

  const aside = (
    <div className="flight-offer-compact-summary__accordion-aside">
      <div className="flight-offer-compact-summary__accordion-aside-leading">
        <HiOutlinePencilSquare
          className="flight-offer-compact-summary__accordion-aside-icon"
          aria-hidden
        />
        <div className="flight-offer-compact-summary__accordion-aside-text">
          <span className="flight-offer-compact-summary__accordion-primary">
            {editorOpen ? "סגור חיפוש" : triggerLabel}
          </span>
          {!editorOpen ? (
            <span className="flight-offer-compact-summary__accordion-hint">
              {collapsedHint}
            </span>
          ) : null}
        </div>
      </div>
      <HiOutlineChevronDown
        className={`flight-offer-compact-summary__accordion-chevron${editorOpen ? " is-expanded" : ""}`}
        aria-hidden
      />
    </div>
  );

  return (
    <div
      className={`flight-offer-compact-summary ${variantClass} ${className}`.trim()}
      dir="rtl"
    >
      {onEditSearch ? (
        <button
          type="button"
          id={accordionHeaderId}
          className={`flight-offer-compact-summary__accordion-trigger${editorOpen ? " is-expanded" : ""}`}
          onClick={handleAccordionActivate}
          aria-expanded={editorOpen}
          aria-controls={accordionControlsId || undefined}
          aria-label={
            editorOpen
              ? "סגור את אזור החיפוש והפילטרים"
              : `${triggerLabel} — ${collapsedHint}`
          }
        >
          {summaryGrid}
          {aside}
        </button>
      ) : (
        summaryGrid
      )}
    </div>
  );
}
